import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Route } from '@app/core/enums/route.enum';
import { AppConfigService } from '@app/core/services/config.service';
import { AuthService } from '@auth0/auth0-angular';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Observable, map, of, switchMap, take, timeout } from 'rxjs';
import { AnalyticsEvents } from '../enums/analytics-events.enum';
import { FeedbackSandbox } from '../sandboxes/feedback.sandbox';
import { DeviceType } from '../types/common/device.types';
import { AnalyticsService } from './analytics.service';
import { NotificationService } from './notification.service';
import { CampaignSandbox } from '../sandboxes/campaign.sandbox';
import { HomeSandbox } from '@app/home/sandboxes/home.sandbox';
import { DashboardSandbox } from '@app/dashboard/sandboxes/dashboard.sandbox';
import { datadogRum } from '@datadog/browser-rum';
import { SignalRService } from './signalr.service';
import { InvitationService } from './invitation.service';
import { StorageVariables } from '@app/shared/enums/storage.enums';
import { RegistrationData } from '@app/core/types/registration-data.types';
import { HttpCacheService } from './http-cache.service';
import { NavController } from '@ionic/angular';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';

const SESSION_EXPIRED_ERRORS = ['missing_refresh_token', 'login_required', 'invalid_grant'];

/**
 * Whether an error means the Auth0 session can no longer be refreshed (refresh
 * token expired, revoked or missing). The auth interceptor emits it on `error$`,
 * which logs the user out, and also rethrows it to whoever made the request.
 */
export const isSessionExpiredError = (error: unknown): boolean => {
  const code = (error as { error?: unknown } | null)?.error;
  return typeof code === 'string' && SESSION_EXPIRED_ERRORS.includes(code);
};

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  readonly authenticated$: Observable<boolean> = this.authService.idTokenClaims$.pipe(map(Boolean));

  // App-lifetime signal mirroring `authenticated$`. Created once on this root
  // singleton, so it holds the resolved auth state by the time any page renders
  // and never resets between navigations. Pages read this instead of creating a
  // per-page `toSignal(authenticated$)` (which starts `undefined` on every
  // navigation, mounting auth-conditional header buttons a tick late and making
  // the collapsible header jump). This only *adds* a subscriber — it does not
  // multicast/replace `authenticated$`, so the guard's fresh-on-subscribe read
  // stays intact.
  readonly isAuthenticated = toSignal(this.authenticated$);

  private readonly invitationService = inject(InvitationService);
  private readonly httpCacheService = inject(HttpCacheService);
  private readonly translationSandbox = inject(TranslationSandbox);
  private readonly router = inject(Router);
  // inject() rather than a constructor param: TS's emitDecoratorMetadata captures
  // constructor parameter types as a module-top-level `design:paramtypes` array evaluated
  // at class-decoration time, which deadlocks on the circular
  // authentication.service.ts -> notification.service.ts -> action.base.ts -> ... chain
  // ("Cannot access 'NotificationService' before initialization") under bundlers that
  // pick a different module evaluation order than the app's normal single entry point
  // (e.g. Storybook, where every story file is its own entry point). inject() resolves
  // lazily inside the field initializer instead, sidestepping the module-load-time cycle.
  private readonly notificationService = inject(NotificationService);

  constructor(
    private configService: AppConfigService,
    private authService: AuthService,
    private feedbackSandbox: FeedbackSandbox,
    private analyticsService: AnalyticsService,
    private readonly campaignSandbox: CampaignSandbox,
    private readonly homeSandbox: HomeSandbox,
    private readonly dashboardSandbox: DashboardSandbox,
    private readonly signalRService: SignalRService,
    private readonly navController: NavController
  ) {
    this.listenToAuth0LoginError();
    this.listenToAuth0UserChanges();
  }

  login(): void {
    this.authService.loginWithRedirect({
      openUrl: (url) => Browser.open({ url, windowName: '_self' }),
      authorizationParams: {
        redirect_uri: this.configService.callbackUri,
        login_hint: localStorage.getItem(StorageVariables.LastLoggedInEmail) ?? undefined,
        prompt: 'login'
      },
    })
  }

  register(email?: string, invitationId?: string): void {
    this.authService.loginWithRedirect({
      openUrl: (url) => Browser.open({ url, windowName: '_self' }),
      authorizationParams: {
        prompt: 'login',
        screen_hint: 'signup',
        login_hint: email,
        redirect_uri: this.configService.callbackUri
      },
      appState: {
        invitationId,
      }
    });
  }

  async logout(): Promise<void> {
    await this.notificationService.unregisterNotifications();
    this.authService.logout({
      openUrl: (url) => Browser.open({ url }),
      clientId: this.configService.config.security.auth0.clientId,
      logoutParams: { returnTo: this.configService.callbackUri },
    })
    .pipe(take(1))
    .subscribe(async () => {
      datadogRum.clearUser();
      this.homeSandbox.cleanUpListeners();
      this.dashboardSandbox.cleanUpListeners();
      this.notificationService.cleanUpListeners();
      this.invitationService.clearInvitationContext();
      this.signalRService.cleanUpAppStateChangeListener();
      this.feedbackSandbox.removeAppStateChangeListener();
      this.httpCacheService.clear();
      this.analyticsService.logEvent(AnalyticsEvents.Logout);
      this.navController.navigateRoot([Route.Login]);
      if (Capacitor.getPlatform() !== DeviceType.Android) {
        await Browser.close();
      }
    });
  }

  forceRefreshSession(): void {
    this.authService.getAccessTokenSilently({ cacheMode: 'off' }).subscribe();
  }

  async handleRedirect(url: string): Promise<void> {
    if (!url.includes('callback')) {
      return;
    }

    const includesState = url.includes('state=');
    const includesError = url.includes('error=');
    const includesCode = url.includes('code=');
    const uriHasParams = includesState && (includesError || includesCode);
    if (!uriHasParams) {
      return;
    }

    this.router.navigate([Route.Login, Route.Loading], { replaceUrl: true });

    this.authService
      .handleRedirectCallback(url)
      .pipe(
        timeout({ first: 60_000 }),
        take(1),
        switchMap((state) => this.prefillInvitation(state?.appState?.['invitationId']))
      )
      .subscribe({
        next: () => {
          this.campaignSandbox.checkCampaignInLocalStorage();
          this.router.navigate([Route.Home], { replaceUrl: true });
        },
        error: (error: unknown) => {
          datadogRum.addError(error, { context: 'auth0-handle-redirect' });
          this.router.navigate([Route.UnrecoverableError], {
            replaceUrl: true,
            state: { message: this.translationSandbox.translate('error.unrecoverable.login') },
          });
        },
      });

    this.analyticsService.logEvent(AnalyticsEvents.Login);

    if (Capacitor.getPlatform() !== DeviceType.Android) {
      await Browser.close();
    }
  }

  private prefillInvitation(invitationId: string | undefined): Observable<RegistrationData | undefined> {
    if (!invitationId) {
      return of(undefined);
    }

    localStorage.setItem(StorageVariables.InvitationId, invitationId);
    return this.invitationService.prefillRegistrationData(invitationId);
  }

  private listenToAuth0LoginError(): void {
    // See: https://auth0.github.io/auth0-angular/classes/AuthService.html#error_
    this.authService.error$.subscribe((error) => {
      // When refresh token is expired or there is something wrong with it, redirect to auth0 login page.
      if (isSessionExpiredError(error)) {
        this.logout();
      }
    });
  }

  private listenToAuth0UserChanges(): void {
    this.authService.user$.subscribe((user) => {
      if (!user) {
        return;
      }

      // Auth0 prefixes `sub` with the identity provider (`auth0|…` for database
      // connections, `google-oauth2|…` etc. for social logins). Only remember the
      // email for database users: prefilling it as `login_hint` for a social-only
      // account would steer them to a password login they can't complete.
      const isDatabaseUser = user.sub?.startsWith('auth0|');
      if (isDatabaseUser && user.email) {
        localStorage.setItem(StorageVariables.LastLoggedInEmail, user.email);
      } else {
        localStorage.removeItem(StorageVariables.LastLoggedInEmail);
      }
    });
  }
}
